// src/components/Adviser/Tabs/LicenseCertificates/EditLicenseCertificates.jsx

import React, { useEffect, useMemo, useState, useRef } from "react";
import { PostCard } from "../../HelperFunctions/HelperFunctions";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import Field from "../../../../DesignSystem/Inputs/Field";
// import {
//   getEmptyLicenseCertificates,
//   LICENSE_CERTIFICATE_SECTIONS,
// } from "./LicenseCertificateFields";
import { toDateInputValue, toIsoDateOnly } from "../utils/licenseCertificateDateUtils";
import { saveAdviserLicenseCertificates } from "../../../../../context/Adviser/Utils/SaveAdviserLicenseCertificates";
import "../../../../../css/Adviser/EditAdviserDetailsPage.css";
import Breadcrumb from "../../../../DesignSystem/Breadcrumb/Breadcrumb";
import CustomSelect from "../../../../DesignSystem/Dropdowns/CustomSelect";
import { ReactComponent as DeleteIcon } from "../../../../../assets/icons/delete.svg";
import { ReactComponent as AddIcon } from "../../../../../assets/icons/add.svg";
import {
  COMPETENT_ADVISER_TEST_FIELDS,
  COMPETENT_ADVISER_TEST_OPTIONS,
  DEFAULT_COMPETENT_ADVISER_TEST_KEYS,
  ADVISER_RESPONDED_TOOLTIP,
  getEmptyLicenseCertificates,
  LICENSE_CERTIFICATE_SECTIONS,
  canViewLicenseCertificateField,
} from "../utils/LicenseCertificateFields";
import LicenseCertificateEditLogs from "./LicenseCertificateEditLogs";
import CpdRecordEditor from "./CpdRecordEditor";
import RadioGroup from "../../../../DesignSystem/SelectionControls/RadioGroup";
import NewTextArea from "../../../../DesignSystem/Inputs/NewTextArea";
import CheckboxMultiSelect from "../../../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import { useUserPermissions } from "../../../../SignIn/UserPermissionsContext";
import Tooltip from "../../../../DesignSystem/Tooltip/Tooltip";
import { ReactComponent as InformationIcon } from "../../../../../assets/icons/information.svg";

const FieldLabelWithTooltip = ({ label, tooltip }) => {
  if (!tooltip) return label;

  return (
    <span className="smFieldLabel">
      <span>{label}</span>
      <Tooltip content={tooltip} position="top">
        <InformationIcon
          className="smFieldTooltipIcon"
          aria-label={`${label} help`}
        />
      </Tooltip>
    </span>
  );
};

const mergeLicenseCertificates = (source = {}, adviserEmail = "") => {
  const empty = getEmptyLicenseCertificates();

  const merged = Object.entries(empty).reduce((acc, [sectionKey, sectionValue]) => {
    acc[sectionKey] = {
      ...sectionValue,
      ...(source?.[sectionKey] || {}),
    };

    return acc;
  }, {});

  const savedSps = source?.spsCertificate || {};

  if (!merged.spsCertificate.notifyAdviser) {
    merged.spsCertificate.notifyAdviser =
      savedSps.notify === "customAdviser" && savedSps.customAdviserId
        ? savedSps.customAdviserId
        : adviserEmail;
  }

  return merged;
};

const normaliseLicenseCertificatesForSave = (form) => {
  const normalised = {};

  LICENSE_CERTIFICATE_SECTIONS.forEach((section) => {
    normalised[section.key] = {};

    if (section.key === "competentAdviser") {
      const enabledTests = Array.isArray(form?.competentAdviser?.enabledTests)
        ? form.competentAdviser.enabledTests
        : DEFAULT_COMPETENT_ADVISER_TEST_KEYS;

      normalised.competentAdviser.enabledTests = enabledTests;

      COMPETENT_ADVISER_TEST_FIELDS.forEach((field) => {
        const rawValue = form?.competentAdviser?.[field.key] || "";

        normalised.competentAdviser[field.key] = toIsoDateOnly(rawValue);
      });

      normalised.competentAdviser.notes = String(
        form?.competentAdviser?.notes || "",
      ).trim();
      const adviserResponded = form?.competentAdviser?.adviserResponded;
      normalised.competentAdviser.adviserResponded =
        adviserResponded === true || adviserResponded === false
          ? adviserResponded
          : null;

      return;
    }

    section.fields.forEach((field) => {
      const rawValue = form?.[section.key]?.[field.key] ?? "";

      normalised[section.key][field.key] =
        field.inputType === "booleanRadio"
          ? rawValue === true || rawValue === false
            ? rawValue
            : null
          : field.inputType === "adviserMultiSelect"
          ? Array.isArray(rawValue)
            ? rawValue.filter(Boolean)
            : []
          : field.type === "date"
          ? toIsoDateOnly(rawValue)
          : String(rawValue || "").trim();
    });
  });

  return normalised;
};


const calculateNextOcaMeeting = (lastOcaMeeting) => {
  const isoDate = toIsoDateOnly(lastOcaMeeting);

  if (!isoDate) return "";

  const [year, month, day] = isoDate.split("-").map(Number);
  const targetMonthIndex = month - 1 + 18;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const lastDayOfTargetMonth = new Date(
    targetYear,
    targetMonth + 1,
    0,
  ).getDate();

  return [
    targetYear,
    String(targetMonth + 1).padStart(2, "0"),
    String(Math.min(day, lastDayOfTargetMonth)).padStart(2, "0"),
  ].join("-");
};

const isLicenseCertificateDateField = (sectionKey, fieldKey) => {
  if (sectionKey === "competentAdviser") {
    return COMPETENT_ADVISER_TEST_FIELDS.some(
      (field) => field.key === fieldKey && field.type === "date",
    );
  }

  const section = LICENSE_CERTIFICATE_SECTIONS.find(
    (item) => item.key === sectionKey,
  );

  return Boolean(
    section?.fields?.some(
      (field) => field.key === fieldKey && field.type === "date",
    ),
  );
};

const EditLicenseCertificates = ({
  adviserName,
  adviserId,
  adviserEmail,
  details,
  advisers = [],
  loading,
  scrollToSection,
  onSaved,
}) => {
  const { userPermissions = [] } = useUserPermissions();
  const savedValues = useMemo(
    () => details?.licenseCertificates || {},
    [details],
  );

  const [form, setForm] = useState(() =>
    mergeLicenseCertificates(savedValues, adviserEmail),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const adviserOptions = useMemo(
    () =>
      [
        ...(adviserEmail
          ? [
              {
                id: adviserEmail,
                name: adviserName
                  ? `${adviserName} (${adviserEmail})`
                  : adviserEmail,
              },
            ]
          : []),
        ...advisers
          .filter(
            (adviser) =>
              adviser.isAppointed !== false &&
              adviser.id !== adviserId &&
              adviser.email !== adviserEmail,
          )
          .map((adviser) => {
            const name = [
              adviser.preferredName || adviser.firstName,
              adviser.lastName,
            ]
              .filter(Boolean)
              .join(" ")
              .trim();

            return {
              id: adviser.id || adviser.email,
              name: name ? `${name} (${adviser.email})` : adviser.email,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
      ],
    [advisers, adviserId, adviserEmail, adviserName],
  );

  const sectionRefs = useRef({});
  const cpdEditorRef = useRef(null);
  const lastScrolledSectionRef = useRef("");

  const setSectionRef = (sectionKey) => (element) => {
    if (element) {
      sectionRefs.current[sectionKey] = element;
    }
  };

  useEffect(() => {
    if (!scrollToSection || loading) return;

    const scrollKey = `${adviserEmail}-${scrollToSection}`;

    if (lastScrolledSectionRef.current === scrollKey) return;

    const timer = setTimeout(() => {
      const sectionElement = sectionRefs.current[scrollToSection];

      if (!sectionElement) return;

      sectionElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      lastScrolledSectionRef.current = scrollKey;
    }, 300);

    return () => clearTimeout(timer);
  }, [scrollToSection, loading, adviserEmail]);

  useEffect(() => {
    setForm(mergeLicenseCertificates(savedValues, adviserEmail));
  }, [savedValues, adviserEmail]);

  const updateSection = (sectionKey, fieldKey, value) => {
    setForm((prev) => {
      const previousValue = prev?.[sectionKey]?.[fieldKey] ?? "";
      const updatedSection = {
        ...(prev?.[sectionKey] || {}),
        [fieldKey]: value,
      };

      if (
        isLicenseCertificateDateField(sectionKey, fieldKey) &&
        String(previousValue) !== String(value ?? "")
      ) {
        updatedSection.adviserResponded = null;
      }

      if (sectionKey === "tc" && fieldKey === "lastOcaMeeting") {
        updatedSection.nextOcaMeeting = calculateNextOcaMeeting(value);
      }

      return { ...prev, [sectionKey]: updatedSection };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError("");

      if (!adviserId) {
        throw new Error("Adviser ID not found.");
      }

      if (
        !form?.spsCertificate?.notifyAdviser
      ) {
        throw new Error("Please select the adviser to notify for SPS.");
      }

      const payload = normaliseLicenseCertificatesForSave(form);
      const cpdRecords = cpdEditorRef.current?.getRecords?.() || {};

      if (Object.keys(cpdRecords).length > 0) {
        payload.cpd = cpdRecords;
      }

      await saveAdviserLicenseCertificates(adviserId, payload, adviserEmail);

      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (error) {
      console.error("Failed to save license/certificates:", error);
      setSaveError(error.message || "Failed to save licene/certificates.");
    } finally {
      setSaving(false);
    }
  };

  const getEnabledCompetentTests = () => {
    const savedEnabledTests = form?.competentAdviser?.enabledTests;

    if (Array.isArray(savedEnabledTests) && savedEnabledTests.length > 0) {
      return savedEnabledTests;
    }

    return DEFAULT_COMPETENT_ADVISER_TEST_KEYS;
  };

  const updateCompetentEnabledTests = (enabledTests) => {
    setForm((prev) => ({
      ...prev,
      competentAdviser: {
        ...(prev?.competentAdviser || {}),
        enabledTests,
      },
    }));
  };

  const addCompetentTest = (testKey) => {
    if (!testKey) return;

    const enabledTests = getEnabledCompetentTests();

    if (enabledTests.includes(testKey)) return;

    updateCompetentEnabledTests([...enabledTests, testKey]);
  };

  const removeCompetentTest = (testKey) => {
    const enabledTests = getEnabledCompetentTests();

    updateCompetentEnabledTests(
      enabledTests.filter((currentKey) => currentKey !== testKey),
    );
  };

  return (
    <div className="smFeed">
      <Breadcrumb
        items={[
          { label: "Advisers", to: "/advisers" },
          {
            label: adviserEmail || "Adviser",
            to: `/advisers/${encodeURIComponent(adviserEmail)}?tab=licenceCertificates`,
          },
          { label: "Edit Licence Certificates" },
        ]}
      />
      <div className="smEditActions">
        <div className="smEditActionsLeft">
          <h3 className="FP_H3">
            {adviserName || "Edit Licence Certificates"}
          </h3>
        </div>
        <PrimaryButton
          label={saving ? "Saving..." : "Save Details"}
          onClick={handleSave}
          disabled={saving || loading}
        />

      </div>

      {saveError ? (
        <div className="Content-Small adviserLicenseError">{saveError}</div>
      ) : null}

      {LICENSE_CERTIFICATE_SECTIONS.map((section) => {
        if (section.key === "competentAdviser") {
          const enabledTests = getEnabledCompetentTests();

          const selectedFields = COMPETENT_ADVISER_TEST_FIELDS.filter((field) =>
            enabledTests.includes(field.key),
          );

          const availableOptions = COMPETENT_ADVISER_TEST_OPTIONS.filter(
            (option) => !enabledTests.includes(option.id),
          );

          return (
            <div
              key={section.key}
              ref={setSectionRef(section.key)}
              className="lcEditSection"
            >
              <PostCard title={section.title}>
                <div className="smCompetentAdviserToolbar">
                  <CustomSelect
                    label="Add test"
                    options={availableOptions}
                    value=""
                    placeholder="Select test to add"
                    searchable
                    clearable={false}
                    onChange={(event) => addCompetentTest(event.target.value)}
                  />

                  <AddIcon className="smActionIcon" title="Add selected test" />
                </div>

                <div className="smGrid2 align-end">
                  {selectedFields.map((field) => {
                    const rawValue = form?.competentAdviser?.[field.key] || "";

                    return (
                      <div
                        key={`competentAdviser-${field.key}`}
                        className="smDynamicFieldRow"
                      >
                        <div className="smDynamicFieldInput">
                          <Field
                            label={field.label}
                            type="date"
                            value={toDateInputValue(rawValue)}
                            onChange={(value) =>
                              updateSection(
                                "competentAdviser",
                                field.key,
                                toIsoDateOnly(value),
                              )
                            }
                          />
                        </div>

                        <DeleteIcon
                          className="smActionIcon smDeleteIcon"
                          onClick={() => removeCompetentTest(field.key)}
                          title={`Remove ${field.label}`}
                        />
                      </div>
                    );
                  })}

                  <RadioGroup
                    name="competentAdviser-adviserResponded"
                    label={
                      <FieldLabelWithTooltip
                        label="Adviser Responded"
                        tooltip={ADVISER_RESPONDED_TOOLTIP}
                      />
                    }
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    value={form?.competentAdviser?.adviserResponded ?? null}
                    columns={2}
                    deselectable
                    onChange={(value) =>
                      updateSection(
                        "competentAdviser",
                        "adviserResponded",
                        value,
                      )
                    }
                  />

                  <NewTextArea
                    id="competentAdviser-notes"
                    label="Notes"
                    value={form?.competentAdviser?.notes || ""}
                    onChange={(event) =>
                      updateSection(
                        "competentAdviser",
                        "notes",
                        event.target.value,
                      )
                    }
                  />
                </div>
              </PostCard>
            </div>
          );
        }

        return (
          <div
            key={section.key}
            ref={setSectionRef(section.key)}
            className="lcEditSection"
          >
            <PostCard title={section.title}>
              <div className="smGrid2">
                {section.fields
                  .filter((field) =>
                    canViewLicenseCertificateField(field, userPermissions),
                  )
                  .map((field) => {
                  const rawValue = form?.[section.key]?.[field.key] ?? "";

                  if (field.inputType === "booleanRadio") {
                    return (
                      <RadioGroup
                        key={`${section.key}-${field.key}`}
                        name={`${section.key}-${field.key}`}
                        label={
                          <FieldLabelWithTooltip
                            label={field.label}
                            tooltip={field.tooltip}
                          />
                        }
                        options={[
                          { value: true, label: "Yes" },
                          { value: false, label: "No" },
                        ]}
                        value={rawValue}
                        columns={2}
                        deselectable={field.deselectable}
                        onChange={(value) =>
                          updateSection(
                            section.key,
                            field.key,
                            value,
                          )
                        }
                      />
                    );
                  }

                  if (field.inputType === "adviserMultiSelect") {
                    return (
                      <CheckboxMultiSelect
                        key={`${section.key}-${field.key}`}
                        label={field.label}
                        value={Array.isArray(rawValue) ? rawValue : []}
                        options={adviserOptions.map((option) => ({
                          value: option.id,
                          label: option.name,
                        }))}
                        placeholder="Select advisers to CC"
                        helperText="Select advisers to receive a copy"
                        onChange={(value) =>
                          updateSection(section.key, field.key, value)
                        }
                      />
                    );
                  }

                  if (field.inputType === "textarea") {
                    return (
                      <NewTextArea
                        key={`${section.key}-${field.key}`}
                        id={`${section.key}-${field.key}`}
                        label={field.label}
                        value={rawValue}
                        onChange={(event) =>
                          updateSection(
                            section.key,
                            field.key,
                            event.target.value,
                          )
                        }
                      />
                    );
                  }

                  if (
                    field.inputType === "select" ||
                    field.inputType === "adviserSelect"
                  ) {
                    return (
                      <CustomSelect
                        key={`${section.key}-${field.key}`}
                        label={
                          <FieldLabelWithTooltip
                            label={field.label}
                            tooltip={field.tooltip}
                          />
                        }
                        options={
                          field.inputType === "adviserSelect"
                            ? adviserOptions
                            : field.options || []
                        }
                        value={rawValue}
                        placeholder={`Select ${field.label}`}
                        searchable={field.inputType === "adviserSelect"}
                        clearable={field.inputType === "adviserSelect"}
                        onChange={(event) =>
                          updateSection(
                            section.key,
                            field.key,
                            event.target.value,
                          )
                        }
                      />
                    );
                  }

                  return (
                    <Field
                      key={`${section.key}-${field.key}`}
                      label={field.label}
                      type={field.type || "text"}
                      value={
                        field.type === "date"
                          ? toDateInputValue(rawValue)
                          : rawValue
                      }
                      onChange={(value) =>
                        updateSection(
                          section.key,
                          field.key,
                          field.type === "date" ? toIsoDateOnly(value) : value,
                        )
                      }
                    />
                  );
                  })}
              </div>
            </PostCard>
          </div>
        );
      })}
      <div ref={setSectionRef("cpd")} className="lcEditSection">
        <CpdRecordEditor
          ref={cpdEditorRef}
          adviserId={adviserId}
          adviserEmail={adviserEmail}
          details={details}
        />
      </div>
      <LicenseCertificateEditLogs adviserId={adviserId} />
    </div>
  );
};

export default EditLicenseCertificates;
